/**
 * WHAT: Index/Home Page Initialization Module
 * WHY: Handle home page specific functionality
 * HOW: JavaScript module with proper imports and exports
 * IMPORTANCE: Separates concerns and follows industry standards
 */

// Import home page specific functionality
// Muted (not used): import './homeMessages.js'; import './facebookFeed.js';
import './food-showcase.js';
import visitCounterService from './services/visitCounter.service.js';
import { pickArtisanHeroImage } from './utils/heroImageQuality.js';
import {
    escapeUrlForCss,
    loadDeferredTileImages,
    normalizeBackgroundImageUrl,
    retryImageLoad,
    toAbsoluteImageUrl,
    waitForContainerLayout
} from './utils/backgroundImageUrl.js';

/** Same-tab repeat visits: avoid re-downloading large home JSON on every refresh (HTTP cache + this buffer). */
const HOME_BG_SESSION_KEY = 'sd_homeBackgrounds_v2';
const HOME_BG_SESSION_TTL_MS = 3 * 60 * 1000;

/**
 * Fetch home backgrounds once and reuse for hero, food grid, gallery, parallax fallback.
 * Reduces /api/backgrounds/page/home from 3+ calls to 1 for faster load.
 * Uses sessionStorage (short TTL) plus server Cache-Control on GET /page/:page so reloads are cheaper than no-store every time.
 */
async function fetchHomeBackgrounds() {
    try {
        const raw = sessionStorage.getItem(HOME_BG_SESSION_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (
                parsed &&
                typeof parsed.ts === 'number' &&
                Array.isArray(parsed.images) &&
                Date.now() - parsed.ts < HOME_BG_SESSION_TTL_MS
            ) {
                return parsed.images;
            }
        }
    } catch {
        /* ignore corrupt or disabled storage */
    }

    try {
        const response = await fetch('/api/backgrounds/page/home', {
            credentials: 'same-origin',
            cache: 'default'
        });
        if (response.ok) {
            const body = await response.json();
            const payload = body?.data ?? body;
            const images = Array.isArray(payload) ? null : (payload?.images ?? null);
            try {
                if (images && images.length > 0) {
                    sessionStorage.setItem(HOME_BG_SESSION_KEY, JSON.stringify({ ts: Date.now(), images }));
                }
            } catch {
                /* quota / private mode */
            }
            return images;
        }
    } catch (err) {
        console.warn('Home backgrounds fetch (HTTP cache) failed:', err);
    }

    const { data } = await fetchJsonNoCache('/api/backgrounds/page/home');
    const payload = data?.data ?? data;
    const images = Array.isArray(payload) ? null : (payload?.images ?? null);

    try {
        if (images && images.length > 0) {
            sessionStorage.setItem(HOME_BG_SESSION_KEY, JSON.stringify({ ts: Date.now(), images }));
        }
    } catch {
        /* quota / private mode */
    }

    return images;
}

/** #about-image-grid: fixed 120px strip when not full desktop width (767px missed 768–1023 tablets → ~221px tiles). */
const ABOUT_CAROUSEL_COMPACT_MQ = '(max-width: 1023px)';
function isAboutCarouselCompactViewport() {
    return window.matchMedia(ABOUT_CAROUSEL_COMPACT_MQ).matches;
}

function shouldRunMotion() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !document.hidden;
}

function runWhenIdle(task, timeout = 1200) {
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(task, { timeout });
    } else {
        window.setTimeout(task, 0);
    }
}

// Initialize home page: single home API fetch, then parallel inits for speed.
async function initializeHomePage() {
    const isNarrowViewport = window.matchMedia('(max-width: 768px)').matches;

    // One fetch for home backgrounds; awards fetch in parallel (different endpoint).
    const [homeImages, awardsResponse] = await Promise.all([
        fetchHomeBackgrounds(),
        fetch('/api/awards').catch(() => null)
    ]);

    const backgroundContainer = document.getElementById('food-gallery-background');

    // Run all landing inits in parallel (they use shared home data or awards response).
    await Promise.all([
        setupFoodGrid(homeImages),
        setupHeroBackgrounds(homeImages),
        loadAwardImagesWithResponse(awardsResponse),
        backgroundContainer ? setupFoodGalleryBackgroundWithPayload(backgroundContainer, homeImages) : Promise.resolve()
    ]);

    runWhenIdle(() => {
        trackVisit();
    }, 1500);
}

const FOOD_GALLERY_GRID_MAX_ITEMS = 500;
const FOOD_GALLERY_GRID_MIN_ITEMS = 12;

/**
 * WHAT: Compute food gallery grid dimensions (single source of truth for cols, rows, item count)
 * WHY: Avoid duplicated logic in setupFoodGalleryBackgroundWithPayload and fillFoodGalleryGrid
 * HOW: Match landing-optimized.css breakpoints; targetItems = cols * maxRows so grid fills the container
 * @returns {{ cols: number, maxRows: number, targetItems: number }}
 */
function getFoodGalleryGridDimensions(backgroundContainer) {
    const width = backgroundContainer.offsetWidth || document.documentElement.clientWidth || 1024;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1024;
    const style = window.getComputedStyle(backgroundContainer);
    const gap = parseFloat(style.gap) || 6;
    const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0) || 12;
    /* Match landing-optimized.css: 10x5 on phone (<768), 10x10 on desktop (>=768); cell size by breakpoint */
    let cellSize = 64;
    if (viewportWidth <= 480) cellSize = 56;
    else if (viewportWidth >= 768) cellSize = 90;
    const cols = Math.max(2, Math.floor((width - padX + gap) / (cellSize + gap)));
    const maxRows = viewportWidth >= 768 ? 10 : 5;
    const targetItems = Math.min(FOOD_GALLERY_GRID_MAX_ITEMS, Math.max(cols * maxRows, FOOD_GALLERY_GRID_MIN_ITEMS));
    return { cols, maxRows, targetItems };
}

/**
 * WHAT: Fetch JSON without stale 304 responses
 * WHY: 304 responses do not include a response body for fetch()
 * HOW: Force no-store caching and retry with reload on 304
 * IMPORTANCE: Prevents missing background data from cached responses
 */
async function fetchJsonNoCache(url) {
    const requestOptions = {
        cache: 'no-store',
        credentials: 'same-origin'
    };

    let response = null;
    try {
        response = await fetch(url, requestOptions);
        if (response.status === 304) {
            response = await fetch(url, { ...requestOptions, cache: 'reload' });
        }
    } catch (error) {
        console.warn(`Request failed for ${url}:`, error);
        return { response: null, data: null };
    }

    if (!response.ok) {
        return { response, data: null };
    }

    try {
        const data = await response.json();
        return { response, data };
    } catch (error) {
        console.warn(`Failed to parse JSON for ${url}:`, error);
        return { response, data: null };
    }
}

/**
 * WHAT: Setup parallax background
 * WHY: Show stunning food photos in parallax section
 * HOW: Fetch approved background images from API
 * IMPORTANCE: Visual impact that showcases our food
 * NOTE: Hero section now uses slideshow.js for automatic rotation
 */
/**
 * @param {Array|null} homeImages - Optional prefetched home images (from fetchHomeBackgrounds); avoids extra API call.
 */
async function setupHeroBackgrounds(homeImages = null) {
    try {
        if (homeImages && homeImages.length > 0) {
            setupSplitSectionImages(homeImages);
        }
        await setupParallaxLocationImage(homeImages);
    } catch (error) {
        console.warn('Failed to load backgrounds:', error);
    }
}

/**
 * WHAT: Setup parallax section with location image
 * WHY: Use location page image as blurred background for compelling call-to-action
 * HOW: Fetch location data and use first available location image
 * IMPORTANCE: Creates visual connection to physical location
 * @param {Array|null} homeFallback - Optional prefetched home images; used in fallback to avoid refetching /api/backgrounds/page/home.
 */
async function setupParallaxLocationImage(homeFallback = null) {
    try {
        // Paint background on the section (#parallax-quote) so the image shows on the exact element the user wants; inner div is optional for blur
        const parallaxSection = document.getElementById('parallax-quote');
        const parallaxBackground = document.getElementById('parallax-background');
        const parallaxTarget = parallaxSection || parallaxBackground; // section first so image is on the section
        if (!parallaxTarget) {
            console.warn('Parallax section and background div not found');
            return;
        }

        // Ensure section (and optional inner div) is visible
        if (parallaxSection) {
            parallaxSection.style.setProperty('background-size', 'cover', 'important');
            parallaxSection.style.setProperty('background-position', 'center', 'important');
            parallaxSection.style.setProperty('background-repeat', 'no-repeat', 'important');
        }
        if (parallaxBackground) {
            parallaxBackground.style.setProperty('display', 'block', 'important');
            parallaxBackground.style.setProperty('visibility', 'visible', 'important');
            parallaxBackground.style.setProperty('opacity', '1', 'important');
            parallaxBackground.style.setProperty('position', 'absolute', 'important');
            parallaxBackground.style.setProperty('z-index', '0', 'important');
        }

        // Try multiple approaches to get location image
        
        // Approach 1: Get location data from /api/location/active (same endpoint location page uses)
        try {
            
            const { data: locationData, response: locationResponse } = await fetchJsonNoCache('/api/location/active');
            
            
            if (locationData) {
                console.log('Location API response:', locationData);
                
                // Check for backgroundImages in the response structure
                // /api/location/active returns: { success: true, data: { locations: [], backgroundImages: [], settings: {} } }
                let backgroundImages = null;
                if (locationData.data && locationData.data.backgroundImages) {
                    backgroundImages = locationData.data.backgroundImages;
                } else if (locationData.backgroundImages) {
                    backgroundImages = locationData.backgroundImages;
                } else if (locationData.data && locationData.data.locations) {
                    // Fallback: collect images from locations array
                    // Filter to only images enabled for landing page rotation
                    backgroundImages = [];
                    for (const loc of locationData.data.locations) {
                        // Check both loc.images and loc.backgroundImages
                        const locationImages = loc.images || loc.backgroundImages || [];
                        if (Array.isArray(locationImages) && locationImages.length > 0) {
                            // Filter to only active images enabled for landing page rotation
                            const activeImages = locationImages.filter(img => 
                                img.isActive !== false && img.useForLandingPage === true
                            );
                            backgroundImages.push(...activeImages);
                        }
                    }
                }
                
                
                console.log('Found backgroundImages:', backgroundImages);
                
                if (backgroundImages && backgroundImages.length > 0) {
                    // Prefer images explicitly enabled for landing page; fallback to all active images so parallax always shows when any exist
                    const explicitlyForLanding = backgroundImages.filter(img => img.useForLandingPage === true && img.isActive !== false);
                    const landingPageImages = explicitlyForLanding.length > 0
                        ? explicitlyForLanding
                        : backgroundImages.filter(img => img.isActive !== false);
                    
                    if (landingPageImages.length > 0) {
                        // Normalize image URLs
                        const normalizedImages = landingPageImages.map(image => {
                            let imageUrl = image.filename || image.url || image.imageUrl;
                            
                            // Construct full URL if needed - location images are stored in /uploads/location/
                            if (imageUrl) {
                                if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                                    imageUrl = `/uploads/location/${imageUrl}`;
                                } else if (imageUrl.startsWith('/uploads/location/')) {
                                    // Already correct format
                                } else if (imageUrl.startsWith('/uploads/')) {
                                    // Already a full path
                                } else if (image.filename && !imageUrl.includes('/')) {
                                    imageUrl = `/uploads/location/${image.filename}`;
                                }
                            }
                            
                            return imageUrl;
                        }).filter(url => url); // Remove any null/undefined URLs
                        
                        if (normalizedImages.length > 0) {
                            // Set first image immediately so section shows something while preload runs (fixes "content there but not rotating in")
                            const firstUrl = normalizedImages[0];
                            parallaxTarget.style.setProperty('background-image', `url('${firstUrl}')`, 'important');
                            parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                            parallaxTarget.style.setProperty('background-position', 'center', 'important');
                            parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                            console.log('Parallax background set (initial):', firstUrl);

                            // Set up rotation for parallax background images (15 seconds per image)
                            let currentIndex = 0;
                            let failedImages = new Set(); // Track failed images
                            const fallbackImage = '/images/icons/no-image.svg'; // Fallback (file exists in frontend/src/images/icons)
                            
                            // Preload all images for smooth transitions
                            const preloadedImages = [];
                            const preloadPromises = normalizedImages.map((imageUrl, index) => {
                                return new Promise((resolve) => {
                                    const img = new Image();
                                    img.onload = () => {
                                        preloadedImages[index] = img;
                                        console.log(`Preloaded parallax image ${index + 1}/${normalizedImages.length}:`, imageUrl);
                                        resolve(true);
                                    };
                                    img.onerror = () => {
                                        console.warn(`Failed to preload parallax image ${index + 1}:`, imageUrl);
                                        failedImages.add(index);
                                        resolve(false);
                                    };
                                    img.src = imageUrl;
                                });
                            });
                            
                            // Wait for initial preload, then start rotation
                            Promise.all(preloadPromises).then(() => {
                                const availableImages = normalizedImages.filter((_, index) => !failedImages.has(index));
                                
                                if (availableImages.length === 0) {
                                    // All images failed, use fallback
                                    console.warn('All parallax images failed to load, using fallback');
                                    parallaxTarget.style.setProperty('background-image', `url('${fallbackImage}')`, 'important');
                                    parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                                    parallaxTarget.style.setProperty('background-position', 'center', 'important');
                                    parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                                    return;
                                }
                                
                                // Update normalizedImages to only include successful loads
                                normalizedImages.length = 0;
                                normalizedImages.push(...availableImages);
                                currentIndex = 0;
                                
                                const setParallaxImage = (index) => {
                                    const imageUrl = normalizedImages[index];
                                    const nextIndex = (index + 1) % normalizedImages.length;
                                    const nextImageUrl = normalizedImages[nextIndex];
                                    
                                    // Preload next image for smooth transition
                                    if (normalizedImages.length > 1) {
                                        const nextImg = new Image();
                                        nextImg.src = nextImageUrl;
                                    }
                                    
                                    parallaxTarget.style.setProperty('background-image', `url('${imageUrl}')`, 'important');
                                    parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                                    parallaxTarget.style.setProperty('background-position', 'center', 'important');
                                    parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                                    parallaxTarget.style.setProperty('transition', 'opacity 1s ease-in-out', 'important');
                                    console.log('Parallax background set:', imageUrl, `(${index + 1}/${normalizedImages.length})`);
                                    
                                };
                                
                                // Set initial image
                                setParallaxImage(0);
                                
                                // Rotate images every 15 seconds if there are multiple images
                                if (normalizedImages.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                                    // Clear any existing interval
                                    if (window.parallaxRotationInterval) {
                                        clearInterval(window.parallaxRotationInterval);
                                    }
                                    
                                    window.parallaxRotationInterval = setInterval(() => {
                                        if (document.hidden) return;
                                        currentIndex = (currentIndex + 1) % normalizedImages.length;
                                        setParallaxImage(currentIndex);
                                    }, 15000); // 15 seconds
                                    
                                    console.log(`Parallax background rotation started: ${normalizedImages.length} images, rotating every 15 seconds`);
                                }
                            });
                            
                            return;
                        }
                    }
                }
            } else if (locationResponse) {
                console.warn('Location API returned status:', locationResponse.status);
            }
        } catch (err) {
            console.warn('Failed to load from /api/location/active:', err);
        }
        
        // Approach 1b: Fallback to /api/location (singular) if /active doesn't work
        try {
            const { data: locationData, response: locationResponse } = await fetchJsonNoCache('/api/location');
            if (locationData) {
                console.log('Location API (singular) response:', locationData);
                
                // Check for backgroundImages or images in location data
                let backgroundImages = null;
                if (locationData.data && locationData.data.backgroundImages) {
                    backgroundImages = locationData.data.backgroundImages;
                } else if (locationData.data && locationData.data.images) {
                    // Some location APIs return 'images' instead of 'backgroundImages'
                    backgroundImages = locationData.data.images.filter(img => img.isActive !== false);
                } else if (locationData.backgroundImages) {
                    backgroundImages = locationData.backgroundImages;
                } else if (locationData.images) {
                    backgroundImages = locationData.images.filter(img => img.isActive !== false);
                }
                
                console.log('Found backgroundImages from singular endpoint:', backgroundImages);
                
                if (backgroundImages && backgroundImages.length > 0) {
                    // Filter to only images enabled for landing page
                    const landingPageImages = backgroundImages.filter(img => 
                        img.useForLandingPage === true || img.useForLandingPage !== false
                    );
                    
                    if (landingPageImages.length > 0) {
                        // Normalize image URLs
                        const normalizedImages = landingPageImages.map(image => {
                            let imageUrl = image.filename || image.url || image.imageUrl;
                            
                            if (imageUrl) {
                                if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                                    imageUrl = `/uploads/location/${imageUrl}`;
                                } else if (imageUrl.startsWith('/uploads/location/')) {
                                    // Already correct format
                                } else if (imageUrl.startsWith('/uploads/')) {
                                    // Already a full path
                                } else if (image.filename && !imageUrl.includes('/')) {
                                    imageUrl = `/uploads/location/${image.filename}`;
                                }
                            }
                            
                            return imageUrl;
                        }).filter(url => url);
                        
                        if (normalizedImages.length > 0) {
                            // Set up rotation for parallax background images (15 seconds per image)
                            let currentIndex = 0;
                            let failedImages = new Set(); // Track failed images
                            const fallbackImage = '/images/icons/no-image.svg'; // Fallback (file exists in frontend/src/images/icons)
                            
                            // Preload all images for smooth transitions
                            const preloadedImages = [];
                            const preloadPromises = normalizedImages.map((imageUrl, index) => {
                                return new Promise((resolve) => {
                                    const img = new Image();
                                    img.onload = () => {
                                        preloadedImages[index] = img;
                                        console.log(`Preloaded parallax image ${index + 1}/${normalizedImages.length} (singular):`, imageUrl);
                                        resolve(true);
                                    };
                                    img.onerror = () => {
                                        console.warn(`Failed to preload parallax image ${index + 1} (singular):`, imageUrl);
                                        failedImages.add(index);
                                        resolve(false);
                                    };
                                    img.src = imageUrl;
                                });
                            });
                            
                            // Wait for initial preload, then start rotation
                            Promise.all(preloadPromises).then(() => {
                                const availableImages = normalizedImages.filter((_, index) => !failedImages.has(index));
                                
                                if (availableImages.length === 0) {
                                    // All images failed, use fallback
                                    console.warn('All parallax images failed to load (singular), using fallback');
                                    parallaxTarget.style.setProperty('background-image', `url('${fallbackImage}')`, 'important');
                                    parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                                    parallaxTarget.style.setProperty('background-position', 'center', 'important');
                                    parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                                    return;
                                }
                                
                                // Update normalizedImages to only include successful loads
                                normalizedImages.length = 0;
                                normalizedImages.push(...availableImages);
                                currentIndex = 0;
                                
                                const setParallaxImage = (index) => {
                                    const imageUrl = normalizedImages[index];
                                    const nextIndex = (index + 1) % normalizedImages.length;
                                    const nextImageUrl = normalizedImages[nextIndex];
                                    
                                    // Preload next image for smooth transition
                                    if (normalizedImages.length > 1) {
                                        const nextImg = new Image();
                                        nextImg.src = nextImageUrl;
                                    }
                                    
                                    parallaxTarget.style.setProperty('background-image', `url('${imageUrl}')`, 'important');
                                    parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                                    parallaxTarget.style.setProperty('background-position', 'center', 'important');
                                    parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                                    parallaxTarget.style.setProperty('transition', 'opacity 1s ease-in-out', 'important');
                                    console.log('Parallax background set (singular):', imageUrl, `(${index + 1}/${normalizedImages.length})`);
                                    
                                };
                                
                                // Set initial image
                                setParallaxImage(0);
                                
                                // Rotate images every 15 seconds if there are multiple images
                                if (normalizedImages.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                                    // Clear any existing interval
                                    if (window.parallaxRotationInterval) {
                                        clearInterval(window.parallaxRotationInterval);
                                    }
                                    
                                    window.parallaxRotationInterval = setInterval(() => {
                                        if (document.hidden) return;
                                        currentIndex = (currentIndex + 1) % normalizedImages.length;
                                        setParallaxImage(currentIndex);
                                    }, 15000); // 15 seconds
                                    
                                    console.log(`Parallax background rotation started (singular): ${normalizedImages.length} images, rotating every 15 seconds`);
                                }
                            });
                            
                            return;
                        }
                    }
                }
            }
        } catch (err) {
            console.warn('Failed to load from /api/location (singular):', err);
        }

        // Approach 2: Get location page backgrounds
        try {
            const { data: backgroundData, response: backgroundResponse } = await fetchJsonNoCache('/api/backgrounds/page/location');
            if (backgroundData) {
                console.log('Location backgrounds API response:', backgroundData);
                
                // Handle different response structures
                let images = null;
                if (backgroundData.data && backgroundData.data.images) {
                    images = backgroundData.data.images;
                } else if (backgroundData.images) {
                    images = backgroundData.images;
                } else if (Array.isArray(backgroundData.data)) {
                    images = backgroundData.data;
                }
                
                if (images && images.length > 0) {
                    const image = images[0];
                    let imageUrl = image.filename || image.url || image.externalUrl || image.imageUrl;
                    
                    // Construct full URL if needed
                    if (imageUrl) {
                        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                            // Relative path - construct full path
                            if (imageUrl.includes('backgrounds')) {
                                imageUrl = `/uploads/${imageUrl}`;
                            } else {
                                imageUrl = `/uploads/backgrounds/${imageUrl}`;
                            }
                        } else if (imageUrl.startsWith('/uploads/')) {
                            // Already correct format
                        }
                        
                        // Use setProperty to ensure it overrides any CSS
                        parallaxTarget.style.setProperty('background-image', `url('${imageUrl}')`, 'important');
                        parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                        parallaxTarget.style.setProperty('background-position', 'center', 'important');
                        parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                        console.log('Parallax background set from location page backgrounds:', imageUrl);
                        // Verify image loads
                        const img = new Image();
                        img.onload = () => {
                            console.log('Parallax background image loaded successfully:', imageUrl);
                        };
                        img.onerror = () => {
                            console.error('Parallax background image failed to load:', imageUrl);
                        };
                        img.src = imageUrl;
                        return;
                    }
                }
            } else if (backgroundResponse) {
                console.warn('Location backgrounds API returned status:', backgroundResponse.status);
            }
        } catch (err) {
            console.warn('Failed to load from /api/backgrounds/page/location:', err);
        }

        // Approach 3: Get gallery images using the same endpoint that food gallery uses (this works per logs)
        try {
            const { data: galleryData, response: galleryResponse } = await fetchJsonNoCache('/api/gallery/images?activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
            if (galleryData) {
                console.log('Gallery images API response:', galleryData);
                
                // Get images from gallery response
                let images = null;
                if (galleryData.images) {
                    images = galleryData.images;
                } else if (galleryData.data && galleryData.data.images) {
                    images = galleryData.data.images;
                }
                
                if (images && images.length > 0) {
                    // Prefer images tagged for location, but use any if none found
                    const locationImages = images.filter(img => 
                        img.useLocation?.includes('location')
                    );
                    
                    const backgroundImages = images.filter(img => 
                        img.useLocation?.includes('background') || img.useLocation?.includes('anywhere')
                    );
                    
                    // Priority: location images > background images > any image
                    const imageToUse = locationImages[0] || backgroundImages[0] || images[0];
                    let imageUrl = imageToUse.url || imageToUse.filename || imageToUse.imageUrl;
                    
                    // Construct full URL if needed
                    if (imageUrl) {
                        if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                            imageUrl = `/uploads/gallery/${imageUrl}`;
                        } else if (imageUrl.startsWith('/uploads/')) {
                            // Already correct format
                        }
                        
                        // Use setProperty to ensure it overrides any CSS
                        parallaxTarget.style.setProperty('background-image', `url('${imageUrl}')`, 'important');
                        parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                        parallaxTarget.style.setProperty('background-position', 'center', 'important');
                        parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                        console.log('Parallax background set from gallery images:', imageUrl);
                        // Verify image loads
                        const img = new Image();
                        img.onload = () => {
                            console.log('Parallax background image loaded successfully:', imageUrl);
                        };
                        img.onerror = () => {
                            console.error('Parallax background image failed to load:', imageUrl);
                        };
                        img.src = imageUrl;
                        return;
                    }
                } else {
                    console.warn('Gallery images API returned no images');
                }
            } else if (galleryResponse) {
                console.warn('Gallery images API returned status:', galleryResponse.status, galleryResponse.statusText);
            }
        } catch (err) {
            console.warn('Failed to load from gallery images:', err);
        }

        // Fallback: Use home page background images (prefetched when provided)
        try {
            let images = homeFallback && Array.isArray(homeFallback) ? homeFallback : null;
            if (!images || images.length === 0) {
                const { data: homeData } = await fetchJsonNoCache('/api/backgrounds/page/home');
                const payload = homeData?.data ?? homeData;
                images = Array.isArray(payload) ? null : (payload?.images ?? null);
            }
            if (images && images.length > 0) {
                    const image = images[0];
                    const imageUrl = normalizeBackgroundImageUrl(
                        image.filename || image.url || image.externalUrl || image.imageUrl,
                        image
                    );
                    
                    if (imageUrl) {
                        // Use setProperty to ensure it overrides any CSS
                        parallaxTarget.style.setProperty('background-image', `url('${imageUrl}')`, 'important');
                        parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                        parallaxTarget.style.setProperty('background-position', 'center', 'important');
                        parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                        console.log('Parallax background set from home page backgrounds (fallback):', imageUrl);
                        // Verify image loads
                        const img = new Image();
                        img.onload = () => {
                            console.log('Parallax background image loaded successfully:', imageUrl);
                        };
                        img.onerror = () => {
                            console.error('Parallax background image failed to load:', imageUrl);
                        };
                        img.src = imageUrl;
                        return;
                    }
            }
        } catch (err) {
            console.warn('Failed to load fallback home background:', err);
        }

        // Final fallback: Wait a bit for food gallery to load, then use one of those images
        // The food gallery loads images asynchronously, so we need to wait
        setTimeout(async () => {
            try {
                // Try to get an image from the food gallery that was already loaded
                const foodGalleryItems = document.querySelectorAll('.food-gallery-background-item');
                if (foodGalleryItems.length > 0) {
                    const firstItem = foodGalleryItems[0];
                    const tileImg = firstItem.querySelector('img');
                    const imageUrl = tileImg?.src || (() => {
                        const bgImage = window.getComputedStyle(firstItem).backgroundImage;
                        if (!bgImage || bgImage === 'none') return '';
                        const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
                        return urlMatch?.[1] || '';
                    })();
                    if (imageUrl) {
                        parallaxTarget.style.setProperty('background-image', `url('${escapeUrlForCss(imageUrl)}')`, 'important');
                        parallaxTarget.style.setProperty('background-size', 'cover', 'important');
                        parallaxTarget.style.setProperty('background-position', 'center', 'important');
                        parallaxTarget.style.setProperty('background-repeat', 'no-repeat', 'important');
                        console.log('Parallax background set from food gallery image (delayed fallback):', imageUrl);
                        return;
                    }
                }
            } catch (err) {
                console.warn('Failed to get image from food gallery:', err);
            }
            
            // If still no image, set a fallback gradient
            console.warn('No location image found for parallax section, using fallback gradient');
            if (parallaxTarget) {
                parallaxTarget.style.setProperty('background-image', 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', 'important');
            }
        }, 2000); // Wait 2 seconds for food gallery to load
    } catch (error) {
        console.error('Failed to load location image for parallax:', error);
        const section = document.getElementById('parallax-quote');
        if (section) {
            section.style.setProperty('background-image', 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', 'important');
        }
    }
}

/**
 * WHAT: Setup split section images
 * WHY: Populate split-column sections with food images
 * HOW: Assign images from background API to split section image wrappers
 */
async function setupSplitSectionImages(images) {
    // About section now uses a food grid (handled by setupFoodGrid)
    // No need to set static image here

    // Products section now uses slideshow (handled by slideshow.js)
    // No need to set static image here

    // Award images are loaded in initializeHomePage() so they always run (not gated on backgrounds API)
}

/**
 * WHAT: Load award images from API (or use prefetched response)
 * WHY: Display assigned images for award sections; optional response avoids duplicate /api/awards call.
 */
async function loadAwardImagesWithResponse(awardsResponse = null) {
    try {
        const response = awardsResponse ?? await fetch('/api/awards');
        if (!response || !response.ok) {
            if (!response) throw new Error('No award config response');
            throw new Error('Failed to load award config');
        }

        const result = await response.json();
        const config = result.data || result;

        // Runtime evidence: log award config for debugging (remove after fix confirmed)
        if (typeof console !== 'undefined' && console.debug) {
            console.debug('[award] /api/awards response config:', {
                hasAwardImage1: !!config.awardImage1,
                hasAwardImage2: !!config.awardImage2,
                awardImage1: config.awardImage1,
                awardImage2: config.awardImage2
            });
        }

        // Normalize URL: accept string or object with imageUrl/filename; ensure path works from site root
        const toImageUrl = (val) => {
            const raw = typeof val === 'string' ? val : (val && (val.imageUrl || val.filename));
            if (!raw || typeof raw !== 'string') return '';
            const s = raw.trim();
            if (!s) return '';
            return s.startsWith('http') ? s : (s.startsWith('/') ? s : `/${s}`);
        };

        const setAwardSlot = (element, slotConfig) => {
            if (!element) return;
            const imageUrl = toImageUrl(slotConfig);
            if (typeof console !== 'undefined' && console.debug) {
                console.debug('[award] setAwardSlot', element.id, { slotConfig, imageUrl, applied: !!imageUrl });
            }
            if (!imageUrl) return;
            const placeholder = element.querySelector('.award-placeholder');
            const applyBackground = () => {
                element.style.backgroundImage = `url('${imageUrl.replace(/'/g, "\\'")}')`;
                element.style.backgroundSize = 'cover';
                element.style.backgroundPosition = 'center';
                element.style.backgroundRepeat = 'no-repeat';
                if (placeholder) placeholder.style.display = 'none';
            };
            const img = new Image();
            img.onload = applyBackground;
            img.onerror = () => {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('[award] Image failed to load:', imageUrl, element.id);
                }
            };
            img.src = imageUrl.startsWith('/') ? new URL(imageUrl, window.location.origin).href : imageUrl;
        };

        // Load award image 1 (Best Bakery 2025)
        setAwardSlot(document.getElementById('award-image-1'), config.awardImage1);

        // Load award image 2 (Kennewick's Best Cookie)
        setAwardSlot(document.getElementById('award-image-2'), config.awardImage2);
    } catch (error) {
        console.warn('Failed to load award images:', error);
    }
}

/**
 * WHAT: Setup food row for about section
 * WHY: Display 4-9 food images in a single horizontal row
 * HOW: Use prefetched home images when provided, else fetch; fallback to gallery.
 * @param {Array|null} prefetchedHomeImages - Optional raw home API images (from fetchHomeBackgrounds) to avoid extra fetch.
 */
async function setupFoodGrid(prefetchedHomeImages = null) {
    const gridContainer = document.getElementById('about-image-grid');
    if (!gridContainer) {
        console.warn('setupFoodGrid: about-image-grid container not found');
        return;
    }

    try {
        let images = [];
        const homeList = Array.isArray(prefetchedHomeImages) ? prefetchedHomeImages : null;
        if (homeList && homeList.length > 0) {
            images = homeList
                .filter((img) => img?.isActive !== false)
                .map((img) => {
                    const raw = img.filename || img.url || img.imageUrl;
                    const fullUrl = normalizeBackgroundImageUrl(raw, img);
                    return { url: fullUrl, title: img.originalName, source: img.source, externalId: img.externalId };
                })
                .filter((img) => img.url);
            if (images.length > 0) console.log(`Found ${images.length} Base tab images for food grid`);
        }
        if (images.length === 0) {
            const { data: homeData } = await fetchJsonNoCache('/api/backgrounds/page/home');
            const homePayload = homeData?.data ?? homeData;
            const homeImagesList = Array.isArray(homePayload) ? [] : (homePayload?.images || []);
            if (homeImagesList.length > 0) {
                images = homeImagesList
                    .filter((img) => img?.isActive !== false)
                    .map((img) => {
                        const raw = img.filename || img.url || img.imageUrl;
                        const fullUrl = normalizeBackgroundImageUrl(raw, img);
                        return { url: fullUrl, title: img.originalName, source: img.source, externalId: img.externalId };
                    })
                    .filter((img) => img.url);
            }
        }
        // Fallback: if Base has no images, use gallery (food category then any active)
        if (images.length === 0) {
            let res = await fetch('/api/gallery/images?category=food&activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
            if (res.ok) {
                const data = await res.json();
                images = (data.images || []).map((img) => ({
                    url: (img.url && (img.url.startsWith('http') || img.url.startsWith('/'))) ? img.url : `/${img.url || ''}`,
                    title: img.title
                })).filter((img) => img.url);
            }
            if (images.length === 0) {
                res = await fetch('/api/gallery/images?activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
                if (res.ok) {
                    const data = await res.json();
                    images = (data.images || []).map((img) => ({
                        url: (img.url && (img.url.startsWith('http') || img.url.startsWith('/'))) ? img.url : `/${img.url || ''}`,
                        title: img.title
                    })).filter((img) => img.url);
                }
            }
            if (images.length > 0) {
                console.log(`Food grid: no Base images, using ${images.length} gallery images`);
            }
        }

        // Create scrolling carousel: 12 images total, 6 visible at a time
        const totalImages = 12;
        const visibleCount = 6; // Number of visible items at once
        
        // Clear container
        gridContainer.innerHTML = '';
        
        if (images.length === 0) {
            console.warn('No images found for food grid. Creating placeholder items...');
            // Create 12 placeholder items for scrolling
            for (let i = 0; i < totalImages; i++) {
                const gridItem = document.createElement('div');
                gridItem.className = 'food-grid-item';
                gridItem.style.backgroundColor = '#2a2a2a';
                gridItem.setAttribute('aria-label', `Placeholder ${i + 1}`);
                gridContainer.appendChild(gridItem);
            }
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!prefersReducedMotion) {
                startAutoScroll(gridContainer, visibleCount);
            }
            return;
        }
        
        // Calculate daily rotation: use day of year to determine starting index
        // This ensures images rotate daily, showing different images each day
        const today = new Date();
        const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
        const startIndex = images.length > 0 ? dayOfYear % images.length : 0;
        
        // Create items for seamless infinite scroll
        // CRITICAL: For seamless infinite scroll, the pattern must repeat every 6 items (visibleCount)
        // This ensures items 0-5 look identical to items 6-11, 12-17, etc.
        // When we scroll 6 items and reset to 0, items 0-5 appear in the same position as items 6-11 were
        // We'll create enough items to ensure smooth looping - duplicate images if needed
        // Minimum: at least 18 items (3 sets of 6) for smooth scrolling
        const minItemsForSmoothScroll = visibleCount * 3; // 18 items minimum
        const itemsToCreate = images.length >= visibleCount
            ? Math.max(images.length * 2, minItemsForSmoothScroll) // Duplicate all images at least once
            : Math.max(images.length * 3, minItemsForSmoothScroll); // If fewer than 6 images, duplicate more
        
        
        for (let i = 0; i < itemsToCreate; i++) {
            // CRITICAL: Pattern must repeat every 6 items (visibleCount) for seamless loop
            // Use modulo with visibleCount, not totalImages, to ensure pattern repeats correctly
            // This makes items 0-5 identical to items 6-11, 12-17, etc.
            const patternPosition = i % visibleCount; // Position within the 6-item pattern (0-5)
            // If we have fewer images than visibleCount, cycle through available images
            const imageIndex = images.length > 0 ? (startIndex + patternPosition) % images.length : 0;
            const image = images[imageIndex];
            
            // Only log first 12 items to verify pattern (items 0-5 and 6-11 should match)
            if (i < 12) {
            }
            
            if (!image || !image.url) {
                console.warn(`Image ${imageIndex} has no URL:`, image);
                // Create placeholder if image is invalid
                const gridItem = document.createElement('div');
                gridItem.className = 'food-grid-item';
                gridItem.style.backgroundColor = '#2a2a2a';
                gridItem.setAttribute('aria-label', `Placeholder ${i + 1}`);
                gridContainer.appendChild(gridItem);
                continue;
            }
            
            const gridItem = document.createElement('div');
            gridItem.className = 'food-grid-item';
            
            const imageUrl = toAbsoluteImageUrl(image.url);
            if (!imageUrl) continue;

            const bgImg = new Image();
            bgImg.onload = () => {
                gridItem.style.backgroundImage = `url('${escapeUrlForCss(imageUrl)}')`;
            };
            bgImg.onerror = () => retryImageLoad(bgImg, 2);
            bgImg.dataset.src = imageUrl;
            bgImg.src = imageUrl;
            gridItem.style.backgroundSize = 'cover';
            gridItem.style.backgroundPosition = 'center';
            gridItem.style.aspectRatio = '1'; // Ensure square from the start
            gridItem.setAttribute('aria-label', image.title || `Food image ${i + 1}`);
            gridContainer.appendChild(gridItem);
            
            if (i === 0) {
                setTimeout(() => {
                    const rect = gridItem.getBoundingClientRect();
                    const computed = window.getComputedStyle(gridItem);
                }, 100);
            }
        }
        
        // CRITICAL: Set item widths immediately so startAutoScroll doesn't retry infinitely
        // Calculate item size based on container height (items are square, so height = width)
        // This ensures images scale with the viewport since section height uses vh units
        const parent = gridContainer.parentElement;
        if (parent && gridContainer.children.length > 0) {
            // Wait a tiny bit for layout, then set dimensions
            requestAnimationFrame(() => {
                const containerRect = gridContainer.getBoundingClientRect();
                const parentRect = parent.getBoundingClientRect();
                
                // Get gap from computed styles (0.5rem = 8px typically)
                const gap = parseFloat(getComputedStyle(gridContainer).gap) || 8;
                
                // Container dimensions - height scales with viewport (25vh, 22vh, 20vh responsive)
                const containerHeight = containerRect.height || parentRect.height;
                const containerWidth = containerRect.width || parentRect.width;
                // Tablet/narrow: fixed 120px tiles (same as CSS strip). Wide desktop: scale to 6-across.
                const itemSize = isAboutCarouselCompactViewport()
                    ? 120
                    : (() => {
                        const maxWidthBasedSize = (containerWidth - (gap * (visibleCount - 1))) / visibleCount;
                        const heightBasedSize = containerHeight;
                        return Math.min(heightBasedSize, maxWidthBasedSize);
                    })();
                
                // Set all items to square dimensions based on container height
                Array.from(gridContainer.children).forEach(item => {
                    item.style.width = `${itemSize}px`;
                    item.style.minWidth = `${itemSize}px`;
                    item.style.maxWidth = `${itemSize}px`;
                    item.style.height = `${itemSize}px`;
                    item.style.aspectRatio = '1';
                    item.style.flexShrink = '0';
                    item.style.flexGrow = '0';
                });
                
                
                // Start auto-scroll on all devices unless user prefers reduced motion
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                if (!prefersReducedMotion) {
                    startAutoScroll(gridContainer, visibleCount);
                }
            });
        } else {
            console.warn('setupFoodGrid: No parent or items, cannot start auto-scroll');
        }
        
    } catch (error) {
        console.error('Error loading food grid:', error);
        const grid = document.getElementById('about-image-grid');
        if (grid) {
            grid.innerHTML = '';
            const visibleCount = 6;
            for (let i = 0; i < 12; i++) {
                const el = document.createElement('div');
                el.className = 'food-grid-item';
                el.style.backgroundColor = '#2a2a2a';
                el.setAttribute('aria-label', `Placeholder ${i + 1}`);
                grid.appendChild(el);
            }
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!prefersReducedMotion) {
                requestAnimationFrame(() => startAutoScroll(grid, visibleCount));
            }
        }
    }
}

/**
 * WHAT: Auto-scrolling carousel for about section images
 * WHY: Create attention-grabbing effect with smooth scrolling
 * HOW: Use CSS transforms for lightweight smooth scrolling animation
 */
function startAutoScroll(container, visibleCount) {
    
    if (!container || container.children.length === 0) {
        console.warn('startAutoScroll: Container or children missing');
        return;
    }
    
    const items = Array.from(container.children);
    const totalItems = items.length;
    const halfItems = totalItems / 2; // First half is the original set
    
    // Allow scrolling even with fewer items (will just loop through available items)
    // Minimum: at least visibleCount items to show something
    if (totalItems < visibleCount) {
        console.warn('Not enough items for scrolling', { totalItems, required: visibleCount });
        return;
    }
    
    // Adjust scroll logic based on item count
    // If we have fewer than visibleCount * 2 items, we'll scroll through all items and reset
    const needsDuplication = totalItems < visibleCount * 2;
    
    // Calculate item width including gap
    const firstItem = items[0];
    if (!firstItem) {
        console.warn('startAutoScroll: No first item');
        return;
    }
    
    // Variables that need to be accessible in scroll function
    let actualItemWidth = 0;
    let visibleScrollWidth = 0;
    let scrollWidth = 0;
    let parentWidth = 0;
    
    // Wait for layout to be complete
    setTimeout(() => {
        const parent = container.parentElement;
        if (!parent) {
            console.warn('No parent element found');
            return;
        }
        
        const parentRect = parent.getBoundingClientRect();
        const computedGap = getComputedStyle(container).gap;
        const gap = parseFloat(computedGap) || 8;
        const isCompact = isAboutCarouselCompactViewport();

        parentWidth = parentRect.width;

        // Compact viewports: 120px tiles (match setupFoodGrid). Wide: fit 6 items to parent width.
        const itemWidth = isCompact
            ? 120
            : (parentWidth - (gap * (visibleCount - 1))) / visibleCount;

        items.forEach((item) => {
            item.style.width = `${itemWidth}px`;
            item.style.minWidth = `${itemWidth}px`;
            item.style.maxWidth = `${itemWidth}px`;
            item.style.height = `${itemWidth}px`;
            item.style.aspectRatio = '1';
        });
        
        // Re-measure after setting explicit widths
        const actualItemRect = firstItem.getBoundingClientRect();
        actualItemWidth = actualItemRect.width + gap;
        
        
        // Ensure container width is set correctly
        if (parentRect.width === 0 || actualItemRect.width === 0) {
            console.warn('Parent or item width is 0, cannot start animation', { parentWidth: parentRect.width, itemWidth: actualItemRect.width });
            return; // Don't retry - items should have width set before this is called
        }
        
        // CRITICAL: Container must be wider than parent to allow scrolling
        // Set container width to total content width (all items)
        const totalContentWidth = totalItems * actualItemWidth;
        container.style.setProperty('width', `${totalContentWidth}px`, 'important');
        container.style.setProperty('min-width', `${totalContentWidth}px`, 'important');
        container.style.setProperty('max-width', 'none', 'important');
        
        
        // Limit container height to one row (compact: lock 120px so inline height never beats CSS incorrectly)
        let itemHeight = actualItemRect.height;
        if (isCompact) {
            itemHeight = 120;
            container.style.setProperty('height', '120px', 'important');
            container.style.setProperty('max-height', '120px', 'important');
        } else {
            container.style.height = `${itemHeight}px`;
            container.style.maxHeight = `${itemHeight}px`;
        }
        container.style.overflow = 'hidden';

        if (parentRect.height > itemHeight * 1.5 || isCompact) {
            parent.style.setProperty('height', `${itemHeight}px`, 'important');
            parent.style.setProperty('max-height', `${itemHeight}px`, 'important');
            parent.style.overflow = 'hidden';
        }
        
    
        // Set up infinite scroll: scroll visible items, then seamlessly reset
        // HOW IT WORKS FOR SEAMLESS LOOP:
        // - We have 24 items total (12 original + 12 duplicates)
        // - Items 0-11 are first set, items 12-23 are duplicates
        // - We show 6 items at a time
        // - Pattern: items 0-5, 6-11, 12-17, 18-23 should have identical visual pattern
        // - When we scroll 6 items (visibleScrollWidth), items 6-11 are in the same position as items 0-5 were
        // - Resetting to 0 makes items 0-5 appear in the same position, creating seamless infinite scroll
        visibleScrollWidth = actualItemWidth * visibleCount; // Scroll distance for 6 visible items
        scrollWidth = actualItemWidth * halfItems; // Full set width (12 items) for reference
        let currentPosition = 0;
        let isPaused = false;
        let animationId = null;
        /** ~45px/s matches legacy 0.75px/frame at 60Hz; high-refresh monitors no longer run the strip faster or heavier. */
        const CAROUSEL_PX_PER_SEC = 45;
        let lastFrameTime = null;

        // Pause on hover for better UX
        const pauseScroll = () => { 
            isPaused = true; 
        };
        const resumeScroll = () => { 
            isPaused = false; 
        };
        container.addEventListener('mouseenter', pauseScroll);
        container.addEventListener('mouseleave', resumeScroll);
        const onVisibilityChange = () => {
            if (!document.hidden && !isPaused && !animationId) {
                lastFrameTime = null;
                animationId = requestAnimationFrame(scroll);
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        
        // Smooth scroll function using requestAnimationFrame (lightweight)
        // HOW INFINITE SCROLL WORKS:
        // 1. We create 24 items total (12 original + 12 duplicates)
        // 2. Items 0-11 are the first set, items 12-23 are identical duplicates
        // 3. We scroll from left to right, moving the container to the left (negative translateX)
        // 4. When we've scrolled exactly the visible width (6 items), we reset to position 0
        // 5. The reset is invisible because items 6-11 look identical to items 0-5 (within the first set)
        // 6. This creates the illusion of infinite scrolling without any visible jump
        function scroll(frameTime = performance.now()) {
            if (isPaused || !shouldRunMotion()) {
                animationId = null;
                window.setTimeout(() => {
                    if (!container.isConnected) return;
                    lastFrameTime = null;
                    animationId = requestAnimationFrame(scroll);
                }, 320);
                return;
            }

            const deltaMs = lastFrameTime === null
                ? 1000 / 60
                : Math.min(Math.max(0, frameTime - lastFrameTime), 48);
            lastFrameTime = frameTime;

            currentPosition += (CAROUSEL_PX_PER_SEC / 1000) * deltaMs;
            
            // Reset when we've scrolled exactly the visible width (6 items)
            // For seamless infinite scroll: items 0-5 should look identical to items 6-11
            // When we've scrolled 6 items, items 6-11 are in the same visual position as items 0-5 were
            // So resetting to 0 makes items 0-5 appear in the same position, creating seamless loop
            // Reset at exactly visibleScrollWidth for perfect alignment
            if (currentPosition >= visibleScrollWidth) {
                // Reset to 0 for seamless loop - items 0-5 now in same position as items 6-11 were
                currentPosition = currentPosition - visibleScrollWidth;
            }
            
            // Apply transform for smooth scrolling (translate3d for GPU layer, no transition = no lag)
            try {
                container.style.transform = `translate3d(-${currentPosition}px, 0, 0)`;
                container.style.transition = 'none';
            } catch (error) {
                console.error('Error applying transform:', error);
                return;
            }
            
            // Continue animation loop - CRITICAL: This must always be called
            try {
                animationId = requestAnimationFrame(scroll);
                if (!animationId) {
                    console.error('requestAnimationFrame returned null - animation stopped');
                    return; // Stop if RAF fails
                }
            } catch (error) {
                console.error('Error in scroll function:', error);
                return; // Stop on error
            }
        }
        
        // Start scrolling
        lastFrameTime = null;
        animationId = requestAnimationFrame(scroll);
        
        
        // Check animation state after 2 seconds and restart if needed
        setTimeout(() => {
            const isAnimating = animationId !== null;
            // If animation stopped but container exists, restart it
            if (!isAnimating && container.isConnected) {
                lastFrameTime = null;
                animationId = requestAnimationFrame(scroll);
            }
        }, 2000);
        
        // Cleanup function (if needed)
        container._scrollCleanup = () => {
            if (animationId) {
                cancelAnimationFrame(animationId);
            }
            container.removeEventListener('mouseenter', pauseScroll);
            container.removeEventListener('mouseleave', resumeScroll);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, 0);
}

/**
 * WHAT: Track page visit
 * WHY: Privacy-friendly analyticss
 * HOW: One visit per day per browser
 * IMPORTANCE: Analytics without PII
 */
async function trackVisit() {
    try {
        await visitCounterService.incrementVisit();
    } catch (error) {
        console.warn('Visit tracking failed:', error);
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    initializeHomePage().catch(error => {
        console.error('Error initializing home page:', error);
    });

    // Scrolling slideshow (#about-images-section) stays visible on all viewports; CSS handles mobile vs desktop.

});

/**
 * WHAT: Setup products grid for products section
 * WHY: Display prep-tagged photos in a thumbnail grid
 * HOW: Fetch prep images from gallery, display in grid, rotate every 12 seconds
 */
async function setupProductsGrid() {
    const buildCheck = {
        functionExists: typeof setupProductsGrid !== 'undefined',
        codeVersion: 'v2-with-instrumentation',
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        pageUrl: window.location.href
    };
    
    const gridContainer = document.getElementById('products-grid');
    
    
    if (!gridContainer) {
        return;
    }

    const containerRect = gridContainer.getBoundingClientRect();

    try {
        console.log('Loading products grid images...');
        
        // Fetch prep-tagged images (prefer tags or category, fallback to any)
        
        let response = await fetch('/api/gallery/images?tags=prep&activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
        let images = [];
        
        
        if (response.ok) {
            const data = await response.json();
            images = data.images || [];
            
            
            console.log(`Found ${images.length} prep-tagged images`);
        }
        
        // If no prep images, try any active images
        if (images.length === 0) {
            console.log('No prep-tagged images, trying any active images...');
            
            
            response = await fetch('/api/gallery/images?activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
            
            
            if (response.ok) {
                const data = await response.json();
                images = data.images || [];
                
                
                console.log(`Found ${images.length} total active images`);
            }
        }
        
        
        if (images.length === 0) {
            console.warn('No images found for products grid');
            return;
        }
        
        // Clear container
        gridContainer.innerHTML = '';
        
        
        // Create 40 grid items (repeating images to fill the grid)
        const targetItems = 40;
        let itemsCreated = 0;
        for (let i = 0; i < targetItems; i++) {
            // Cycle through available images
            const imageIndex = i % images.length;
            const image = images[imageIndex];
            
            if (!image || !image.url) {
                console.warn(`Image ${imageIndex} has no URL:`, image);
                continue;
            }
            
            const gridItem = document.createElement('div');
            gridItem.className = 'products-grid-item';
            
            // Ensure URL is valid
            const imageUrl = image.url.startsWith('http') || image.url.startsWith('/') 
                ? image.url 
                : `/${image.url}`;
            
            gridItem.style.backgroundImage = `url('${imageUrl}')`;
            gridItem.setAttribute('aria-label', image.title || `Product image ${i + 1}`);
            gridContainer.appendChild(gridItem);
            itemsCreated++;
        }
        
        const finalRect = gridContainer.getBoundingClientRect();
        const itemCount = gridContainer.children.length;
        const firstItem = itemCount > 0 ? gridContainer.children[0] : null;
        const parentRect = gridContainer.parentElement?.getBoundingClientRect();
        const computedContainer = window.getComputedStyle(gridContainer);
        const computedParent = gridContainer.parentElement ? window.getComputedStyle(gridContainer.parentElement) : null;
        
        console.log(`Products grid loaded with ${targetItems} items (repeating ${images.length} images)`);
        
        // Rotate images every 12 seconds (fade transition)
        // Cycle through available images to show different ones
        if (images.length > 1) {
            let rotationOffset = 0;
            setInterval(() => {
                const items = Array.from(gridContainer.querySelectorAll('.products-grid-item'));
                
                // Fade out
                items.forEach(item => {
                    item.style.transition = 'opacity 0.5s ease';
                    item.style.opacity = '0';
                });
                
                // After fade, update images with rotation offset
                setTimeout(() => {
                    rotationOffset = (rotationOffset + 1) % images.length;
                    
                    items.forEach((item, idx) => {
                        const imageIndex = (rotationOffset + idx) % images.length;
                        const image = images[imageIndex];
                        if (image && image.url) {
                            const imageUrl = image.url.startsWith('http') || image.url.startsWith('/') 
                                ? image.url 
                                : `/${image.url}`;
                            item.style.backgroundImage = `url('${imageUrl}')`;
                            item.style.opacity = '1';
                        }
                    });
                }, 500); // Wait for fade out
            }, 12000); // 12 seconds
        }
    } catch (error) {
        console.error('Error loading products grid:', error);
    }
}

/**
 * WHAT: Create one food-gallery mosaic tile using <img> (more reliable than CSS backgrounds on mobile).
 * @param {{ url?: string, source?: string, externalId?: string }} image
 * @returns {HTMLElement | null}
 */
function createFoodGalleryBackgroundItem(image) {
    if (!image?.url) return null;
    const url = normalizeBackgroundImageUrl(image.url, image);
    if (!url) return null;

    const el = document.createElement('div');
    el.className = 'food-gallery-background-item';

    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.setAttribute('aria-hidden', 'true');
    const abs = toAbsoluteImageUrl(url);
    img.dataset.src = abs;
    img.onerror = () => retryImageLoad(img);
    el.appendChild(img);
    return el;
}

/**
 * Start batched tile loads when the mosaic is near the viewport (avoids competing with hero images).
 * @param {HTMLElement} backgroundContainer
 */
function scheduleFoodGalleryTileLoading(backgroundContainer) {
    if (!backgroundContainer || backgroundContainer._foodGalleryLoading) return;

    const startLoad = () => {
        if (backgroundContainer._foodGalleryLoading) return;
        loadDeferredTileImages(backgroundContainer).then(() => {
            verifyFoodGalleryTiles(backgroundContainer);
        });
    };

    const rect = backgroundContainer.getBoundingClientRect();
    if (rect.top < window.innerHeight + 300 && rect.bottom > -100) {
        startLoad();
        return;
    }

    if (typeof IntersectionObserver === 'undefined') {
        startLoad();
        return;
    }

    const io = new IntersectionObserver(
        (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                io.disconnect();
                startLoad();
            }
        },
        { rootMargin: '300px 0px' }
    );
    io.observe(backgroundContainer);
    backgroundContainer._foodGalleryIo = io;

    window.setTimeout(startLoad, 6000);
}

/**
 * WHAT: Fill food gallery grid so every cell has an image; no blank spots.
 * WHY: Grid uses grid-auto-rows so row count = ceil(items/cols). Fewer items (25% reduction) = larger cells, same area.
 * HOW: Use getFoodGalleryGridDimensions; assign normalized absolute URLs via <img> tiles.
 */
function fillFoodGalleryGrid(backgroundContainer) {
    const shuffledImages = backgroundContainer._foodGalleryShuffled;
    if (!shuffledImages || shuffledImages.length === 0) return;
    const width = backgroundContainer.offsetWidth || 0;
    const height = backgroundContainer.offsetHeight || 0;
    if (width <= 0 || height <= 0) {
        scheduleFoodGalleryLayoutRetry(backgroundContainer);
        return;
    }
    if (backgroundContainer._foodGalleryIo) {
        backgroundContainer._foodGalleryIo.disconnect();
        backgroundContainer._foodGalleryIo = null;
    }

    const { targetItems } = getFoodGalleryGridDimensions(backgroundContainer);
    const existingItems = backgroundContainer.querySelectorAll('.food-gallery-background-item');
    existingItems.forEach(item => item.remove());
    for (let i = 0; i < targetItems; i++) {
        const image = shuffledImages[i % shuffledImages.length];
        const el = createFoodGalleryBackgroundItem(image);
        if (el) backgroundContainer.appendChild(el);
    }
    scheduleFoodGalleryTileLoading(backgroundContainer);
}

/**
 * WHAT: Retry grid fill when layout was 0×0 on first paint or images failed to decode.
 * @param {HTMLElement} backgroundContainer
 */
function scheduleFoodGalleryLayoutRetry(backgroundContainer) {
    if (!backgroundContainer || backgroundContainer._foodGalleryLayoutRetryScheduled) return;
    backgroundContainer._foodGalleryLayoutRetryScheduled = true;
    waitForContainerLayout(backgroundContainer).then(() => {
        backgroundContainer._foodGalleryLayoutRetryScheduled = false;
        fillFoodGalleryGrid(backgroundContainer);
        verifyFoodGalleryTiles(backgroundContainer);
    });
}

/**
 * @param {HTMLElement} backgroundContainer
 */
function verifyFoodGalleryTiles(backgroundContainer) {
    if (!backgroundContainer?._foodGalleryShuffled?.length) return;
    if (backgroundContainer._foodGalleryLoading) {
        window.setTimeout(() => verifyFoodGalleryTiles(backgroundContainer), 1500);
        return;
    }

    const imgs = [...backgroundContainer.querySelectorAll('.food-gallery-background-item img')];
    const stillLoading = imgs.filter((img) => !img.complete);
    if (stillLoading.length > 0) {
        if (!backgroundContainer._foodGalleryVerifyTimer) {
            backgroundContainer._foodGalleryVerifyTimer = window.setTimeout(() => {
                backgroundContainer._foodGalleryVerifyTimer = null;
                verifyFoodGalleryTiles(backgroundContainer);
            }, 2500);
        }
        return;
    }

    const broken = imgs.filter((img) => img.naturalWidth === 0 && img.dataset.src);
    broken.forEach((img) => retryImageLoad(img));

    const unloaded = imgs.filter((img) => !img.src && img.dataset.src);
    if (unloaded.length > 0) {
        loadDeferredTileImages(backgroundContainer);
        return;
    }

    const { targetItems } = getFoodGalleryGridDimensions(backgroundContainer);
    const itemCount = backgroundContainer.querySelectorAll('.food-gallery-background-item').length;
    if (itemCount < Math.min(targetItems, FOOD_GALLERY_GRID_MIN_ITEMS)) {
        fillFoodGalleryGrid(backgroundContainer);
    }
}

/**
 * WHAT: Hook load + delayed checks so mobile first visit still fills the mosaic.
 * @param {HTMLElement} backgroundContainer
 */
function bindFoodGalleryLoadGuards(backgroundContainer) {
    if (!backgroundContainer || backgroundContainer._foodGalleryLoadGuardsBound) return;
    backgroundContainer._foodGalleryLoadGuardsBound = true;

    const runVerify = () => verifyFoodGalleryTiles(backgroundContainer);

    if (document.readyState === 'complete') {
        window.setTimeout(runVerify, 3000);
    } else {
        window.addEventListener('load', () => window.setTimeout(runVerify, 3000), { once: true });
    }
    window.setTimeout(runVerify, 8000);
}

/**
 * WHAT: Setup food gallery background grid (with optional prefetched home images)
 * WHY: Display many small images behind the text; single home fetch when used from init.
 * @param {HTMLElement} backgroundContainer - #food-gallery-background element
 * @param {Array|null} prefetchedHomeImages - Optional raw home API images to avoid extra fetch.
 */
async function setupFoodGalleryBackgroundWithPayload(backgroundContainer, prefetchedHomeImages = null) {
    if (!backgroundContainer) return;

    const artisanBackground = document.getElementById('artisan-background');
    if (artisanBackground) {
        artisanBackground.querySelectorAll('.food-gallery-background-item').forEach(item => item.remove());
    }

    try {
        let images = [];
        const rawList = Array.isArray(prefetchedHomeImages) ? prefetchedHomeImages : [];
        if (rawList.length > 0) {
            images = rawList
                .filter((img) => img?.isActive !== false)
                .map((img) => {
                    const raw = img.filename || img.url || img.imageUrl;
                    const url = normalizeBackgroundImageUrl(raw, img);
                    return {
                        url,
                        dimensions: img.dimensions || null,
                        source: img.source,
                        externalId: img.externalId
                    };
                })
                .filter((img) => img.url);
            if (images.length > 0) console.log(`Found ${images.length} Base tab images for food gallery background`);
        }
        if (images.length === 0) {
            const { data: homeData } = await fetchJsonNoCache('/api/backgrounds/page/home');
            const payload = homeData?.data ?? homeData;
            const fallbackRaw = Array.isArray(payload) ? [] : (payload?.images || []);
            if (fallbackRaw.length > 0) {
                images = fallbackRaw
                    .filter((img) => img?.isActive !== false)
                    .map((img) => {
                        const raw = img.filename || img.url || img.imageUrl;
                        const url = normalizeBackgroundImageUrl(raw, img);
                        return {
                            url,
                            dimensions: img.dimensions || null,
                            source: img.source,
                            externalId: img.externalId
                        };
                    })
                    .filter((img) => img.url);
            }
        }
        if (images.length === 0) {
            const galleryRes = await fetch('/api/gallery/images?activeOnly=true&excludeMenuOnly=true&excludeGalleryOnly=true');
            if (galleryRes.ok) {
                const galleryData = await galleryRes.json();
                const galleryImages = galleryData.images || [];
                images = galleryImages.map((img) => ({
                    url: normalizeBackgroundImageUrl(img.url || img.filename)
                })).filter((img) => img.url);
                if (images.length > 0) console.log(`Food gallery background: no Base images, using ${images.length} gallery images`);
            }
        }
        if (images.length === 0) {
            console.warn('No images found for food gallery background');
            return;
        }

        // Shuffle images once; reuse on resize so we don't refetch (important for mobile rotate)
        const shuffledImages = [...images];
        for (let i = shuffledImages.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledImages[i], shuffledImages[j]] = [shuffledImages[j], shuffledImages[i]];
        }
        backgroundContainer._foodGalleryShuffled = shuffledImages;

        // Mobile first paint often reports 0×0 until layout settles; wait before filling the mosaic.
        await waitForContainerLayout(backgroundContainer);
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        fillFoodGalleryGrid(backgroundContainer);
        bindFoodGalleryLoadGuards(backgroundContainer);

        const { cols, maxRows, targetItems } = getFoodGalleryGridDimensions(backgroundContainer);
        const itemsCreated = backgroundContainer.querySelectorAll('.food-gallery-background-item').length;
        console.log(`Food gallery background: ${itemsCreated} cells (${cols} cols, ~${maxRows} rows, target ${targetItems}), ${images.length} images in rotation`);

        // Refill grid on resize/rotate so desktop and mobile always have a full grid (no empty cells)
        if (typeof ResizeObserver !== 'undefined') {
            const ro = backgroundContainer._foodGalleryResizeObserver;
            if (ro) ro.disconnect();
            let resizeT = null;
            backgroundContainer._foodGalleryResizeObserver = new ResizeObserver(() => {
                if (backgroundContainer._foodGalleryLoading) return;
                clearTimeout(resizeT);
                resizeT = setTimeout(() => fillFoodGalleryGrid(backgroundContainer), 250);
            });
            backgroundContainer._foodGalleryResizeObserver.observe(backgroundContainer);
        } else {
            window.removeEventListener('resize', backgroundContainer._foodGalleryResizeHandler);
            let resizeT = null;
            backgroundContainer._foodGalleryResizeHandler = () => {
                clearTimeout(resizeT);
                resizeT = setTimeout(() => fillFoodGalleryGrid(backgroundContainer), 150);
            };
            window.addEventListener('resize', backgroundContainer._foodGalleryResizeHandler);
        }
        
        // Setup single background image for artisan section (NO grid items)
        const artisanEl = document.getElementById('artisan-background');
        if (artisanEl) {
            artisanEl.querySelectorAll('.food-gallery-background-item').forEach(item => item.remove());
            if (images.length > 0) {
                const img = pickArtisanHeroImage(images);
                if (img?.url) {
                    const url = normalizeBackgroundImageUrl(img.url, img);
                    if (url) artisanEl.style.setProperty('background-image', `url('${escapeUrlForCss(url)}')`);
                    artisanEl.style.setProperty('background-size', 'cover');
                    artisanEl.style.setProperty('background-position', 'center');
                    artisanEl.style.setProperty('background-repeat', 'no-repeat');
                    console.log('Artisan section background image set');
                }
            }
        }
    } catch (error) {
        console.error('Error loading food gallery background:', error);
    }
}

/** Call with no args when not using prefetched data (e.g. from other pages). */
async function setupFoodGalleryBackground() {
    return setupFoodGalleryBackgroundWithPayload(document.getElementById('food-gallery-background'), null);
}

// Export for potential use in other modules
/**
 * WHAT: Setup location images at bottom of landing page
 * WHY: Display location images in the about-images-section at the bottom
 * HOW: Fetch location images enabled for landing page and display in carousel
 * IMPORTANCE: Show location images at bottom of landing page
 */
async function setupLocationImagesBottom() {
    const gridContainer = document.getElementById('about-image-grid');
    
    if (!gridContainer) {
        console.warn('setupLocationImagesBottom: about-image-grid container not found');
        return;
    }
    
    try {
        console.log('Loading location images for bottom section...');
        
        // Fetch location images enabled for landing page
        const { data: locationData } = await fetchJsonNoCache('/api/location/active');
        
        if (!locationData || !locationData.data) {
            console.warn('No location data found for bottom images');
            return;
        }
        
        // Get backgroundImages from location data
        let backgroundImages = [];
        if (locationData.data.backgroundImages) {
            backgroundImages = locationData.data.backgroundImages;
        } else if (locationData.backgroundImages) {
            backgroundImages = locationData.backgroundImages;
        } else if (locationData.data.locations) {
            // Collect images from locations array
            locationData.data.locations.forEach(location => {
                if (location.backgroundImages && Array.isArray(location.backgroundImages)) {
                    location.backgroundImages.forEach(img => {
                        if (img.useForLandingPage === true) {
                            backgroundImages.push(img);
                        }
                    });
                }
            });
        }
        
        // Filter to only images enabled for landing page bottom
        const landingPageImages = backgroundImages.filter(img => 
            img.useForLandingPage === true || img.useForLandingPage !== false
        );
        
        if (landingPageImages.length === 0) {
            console.log('No location images enabled for landing page bottom, will use food grid fallback');
            return false; // Return false to indicate no location images, use fallback
        }
        
        // Normalize image URLs
        const normalizedImages = landingPageImages.map(image => {
            let imageUrl = image.filename || image.url || image.imageUrl;
            
            if (imageUrl) {
                if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
                    imageUrl = `/uploads/location/${imageUrl}`;
                } else if (imageUrl.startsWith('/uploads/location/')) {
                    // Already correct format
                } else if (imageUrl.startsWith('/uploads/')) {
                    // Already a full path
                } else if (image.filename && !imageUrl.includes('/')) {
                    imageUrl = `/uploads/location/${image.filename}`;
                }
            }
            
            return imageUrl;
        }).filter(url => url);
        
        if (normalizedImages.length === 0) {
            console.log('No valid location image URLs found, will use food grid fallback');
            return false; // Return false to indicate no location images, use fallback
        }
        
        // Clear container
        gridContainer.innerHTML = '';
        
        // Create scrolling carousel: use all location images, 6 visible at a time
        const totalImages = normalizedImages.length;
        const visibleCount = Math.min(6, totalImages);
        
        // Create grid items for each location image
        for (let i = 0; i < totalImages; i++) {
            const gridItem = document.createElement('div');
            gridItem.className = 'food-grid-item';
            gridItem.style.backgroundImage = `url('${normalizedImages[i]}')`;
            gridItem.style.backgroundSize = 'cover';
            gridItem.style.backgroundPosition = 'center';
            gridItem.style.backgroundRepeat = 'no-repeat';
            gridItem.setAttribute('aria-label', `Location image ${i + 1}`);
            gridContainer.appendChild(gridItem);
        }
        
        console.log(`Loaded ${totalImages} location images into bottom section`);
        
        // Setup auto-scroll if we have more images than visible
        if (totalImages > visibleCount) {
            startAutoScroll(gridContainer, visibleCount);
        }
        
        return true; // Return true to indicate location images were loaded
    } catch (error) {
        console.error('Error loading location images for bottom:', error);
        return false; // Return false on error, use fallback
    }
}

export { initializeHomePage, setupFoodGrid, setupProductsGrid, setupFoodGalleryBackground, setupLocationImagesBottom };
